/**
 * Side-effect-free registry of the dev-instrumentation toggles that flip on/off
 * from a localStorage flag. This is the single source of truth for those storage
 * keys — the patch modules import their key from here, and UI surfaces (e.g. the
 * DevBar) can import the registry to render toggle controls without pulling in
 * any of the side-effecting patch modules.
 *
 * Helpers that need a DOM element (component profiling, reactViz) are
 * intentionally excluded — they can't be driven from a simple on/off control.
 */
import { confidenceDevStorageKey } from './confidenceDev';

export const REACT_DEV_SOURCE_STORAGE_KEY =
  confidenceDevStorageKey('react-dev-source');
export const REACT_SCAN_STORAGE_KEY = confidenceDevStorageKey('react-scan');
export const MSW_STORAGE_KEY = confidenceDevStorageKey('msw');
export const GRAPHQL_LOG_STORAGE_KEY = confidenceDevStorageKey('graphql-log');

export type DevToggleId = 'reactScan' | 'msw' | 'graphqlLog' | 'reactDevSource';

export interface DevToggle {
  id: DevToggleId;
  label: string;
  description: string;
  storageKey: string;
}

export const DEV_TOGGLES: readonly DevToggle[] = [
  {
    id: 'reactScan',
    label: 'react-scan',
    description:
      'Highlight re-rendering components with the react-scan overlay.',
    storageKey: REACT_SCAN_STORAGE_KEY,
  },
  {
    id: 'msw',
    label: 'Mock service worker',
    description: 'Intercept network requests with the app MSW handlers.',
    storageKey: MSW_STORAGE_KEY,
  },
  {
    id: 'graphqlLog',
    label: 'GraphQL logging',
    description: 'Log every GraphQL operation name to the console.',
    storageKey: GRAPHQL_LOG_STORAGE_KEY,
  },
  {
    id: 'reactDevSource',
    label: 'React dev source',
    description: 'Tag DOM nodes with their source file and line.',
    storageKey: REACT_DEV_SOURCE_STORAGE_KEY,
  },
];

export const isDevToggleEnabled = (storageKey: string): boolean =>
  typeof window !== 'undefined' &&
  window.localStorage.getItem(storageKey) === 'true';

/**
 * Flips a toggle and reloads the page so the patch picks up the new state on the
 * next boot — the same set-and-reload behavior the `__confidenceDev` console
 * helpers use.
 */
export const setDevToggleEnabled = (
  storageKey: string,
  enabled: boolean,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (enabled) {
    window.localStorage.setItem(storageKey, 'true');
  } else {
    window.localStorage.removeItem(storageKey);
  }
  window.location.reload();
};
